'use client';

import type { ComponentProps } from 'react';

import { Menu as DropdownMenuPrimitive } from '@base-ui-components/react/menu';
import { clsxm } from '@vs-shop/utils';

export const DropdownMenu = (props: ComponentProps<typeof DropdownMenuPrimitive.Root>) => (
  <DropdownMenuPrimitive.Root data-slot="dropdown-menu" {...props} />
);

export const DropdownMenuPortal = (props: ComponentProps<typeof DropdownMenuPrimitive.Portal>) => (
  <DropdownMenuPrimitive.Portal data-slot="dropdown-menu-portal" {...props} />
);

export const DropdownMenuTrigger = (
  props: ComponentProps<typeof DropdownMenuPrimitive.Trigger>
) => <DropdownMenuPrimitive.Trigger data-slot="dropdown-menu-trigger" {...props} />;

// Base UI splits Radix's `Content` into Positioner (placement) + Popup (the
// styled surface), like Tooltip/Popover. The positioning props are lifted onto
// the Positioner; everything else lands on the Popup. The open animation runs on
// Base UI's `data-starting-style` / `data-ending-style` + `--transform-origin`
// (Radix's `data-[state]` + `--radix-*` vars are gone). `--available-height` and
// `--anchor-width` are Positioner CSS vars, inherited by the Popup.
export type DropdownMenuContentProps = ComponentProps<typeof DropdownMenuPrimitive.Popup> &
  Pick<
    ComponentProps<typeof DropdownMenuPrimitive.Positioner>,
    'side' | 'sideOffset' | 'align' | 'alignOffset'
  >;

export const DropdownMenuContent = ({
  className,
  side,
  sideOffset = 8,
  align = 'start',
  alignOffset,
  children,
  ...props
}: DropdownMenuContentProps) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Positioner
      data-slot="dropdown-menu-positioner"
      className="z-50"
      sideOffset={sideOffset}
      align={align}
      {...(side === undefined ? {} : { side })}
      {...(alignOffset === undefined ? {} : { alignOffset })}
    >
      <DropdownMenuPrimitive.Popup
        data-slot="dropdown-menu-content"
        className={clsxm(
          'bg-background ring-foreground/20 w-full overflow-x-hidden overflow-y-auto rounded-lg px-4 py-2 ring',
          'max-h-(--available-height) origin-(--transform-origin)',
          'transition-[transform,scale,opacity] duration-150',
          'data-starting-style:scale-95 data-starting-style:opacity-0',
          'data-ending-style:scale-95 data-ending-style:opacity-0',
          className
        )}
        {...props}
      >
        {children}
      </DropdownMenuPrimitive.Popup>
    </DropdownMenuPrimitive.Positioner>
  </DropdownMenuPrimitive.Portal>
);

export const DropdownMenuGroup = (props: ComponentProps<typeof DropdownMenuPrimitive.Group>) => (
  <DropdownMenuPrimitive.Group data-slot="dropdown-menu-group" {...props} />
);

export type DropdownMenuItemProps = ComponentProps<typeof DropdownMenuPrimitive.Item> & {
  inset?: boolean;
};

// Radix highlighted Item state was `:focus`; Base UI exposes it as
// `data-highlighted` (no DOM focus is moved), so the active styling keys off
// `data-[highlighted]` instead.
export const DropdownMenuItem = ({ className, inset, ...props }: DropdownMenuItemProps) => (
  <DropdownMenuPrimitive.Item
    data-slot="dropdown-menu-item"
    data-inset={inset}
    className={clsxm(
      'font-heading',
      'relative flex cursor-default items-center gap-2 rounded-sm px-3 py-4 outline-hidden transition-colors select-none',
      'data-[highlighted]:bg-primary/10 data-[highlighted]:text-primary-intense',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8',
      "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
      className
    )}
    {...props}
  />
);

export type DropdownMenuLabelProps = ComponentProps<typeof DropdownMenuPrimitive.GroupLabel> & {
  inset?: boolean;
};

// Base UI only has a group-scoped label (`GroupLabel`); Radix's standalone
// `Label` maps onto it. Kept under the shadcn-familiar `DropdownMenuLabel` name.
export const DropdownMenuLabel = ({ className, inset, ...props }: DropdownMenuLabelProps) => (
  <DropdownMenuPrimitive.GroupLabel
    data-slot="dropdown-menu-label"
    data-inset={inset}
    className={clsxm('px-2 py-1.5 text-sm font-medium data-[inset]:pl-8', className)}
    {...props}
  />
);

export const DropdownMenuSeparator = ({
  className,
  ...props
}: ComponentProps<typeof DropdownMenuPrimitive.Separator>) => (
  <DropdownMenuPrimitive.Separator
    data-slot="dropdown-menu-separator"
    className={clsxm('bg-border -mx-1 my-1 h-px', className)}
    {...props}
  />
);

export const DropdownMenuShortcut = ({ className, ...props }: ComponentProps<'span'>) => (
  <span
    data-slot="dropdown-menu-shortcut"
    className={clsxm('ml-auto text-xs tracking-widest', className)}
    {...props}
  />
);
